import type { RacerInput, TackleState, WallDrop, MudDrop } from '@/sim/SimCore';
import { POWERUP_KINDS, type PowerupKind } from '@/physics/powerups';
import { VEHICLE_ROSTER } from '@/config/racers';
import type { RaceState } from '@/sim/SimCore';
import type { RacerSnapshot, Snapshot } from '@/net/protocol';

/**
 * codec.ts — the BINARY wire format for the two hot online paths (M52):
 *
 *   frame 0x01  snapshot     server → client, 20 Hz  (~700 B vs ~4,200 B JSON)
 *   frame 0x02  input batch  client → server, 30 Hz  (~20 B  vs 94 B × 120/s)
 *
 * Everything else (welcome/lobby/results/…) stays JSON — those are rare
 * control messages where readability beats bytes. Numbers ride as float32:
 * at track scale (~3,000 units) f32 is sub-millimetre, far below the render
 * offsets that smooth every correction anyway.
 *
 * DETERMINISM RULE — input quantization: the client quantizes its control to
 * the wire grid (i8, 1/127 steps) BEFORE predicting with it, so the value the
 * server applies is bit-identical to the value the client replayed. Quantizing
 * only on the wire would make every replay diverge. See {@link quantizeInput}.
 *
 * Batches are REDUNDANT: each carries the last N ticks of input, so a lost or
 * late packet can no longer eat a one-tick edge (the M32 boost-fire bug class
 * dies on the wire too — the room ORs unseen `useItem` edges, same stickiness).
 */

export const FRAME_SNAPSHOT = 0x01;
export const FRAME_INPUT_BATCH = 0x02;
/** V2 Phase C (M108): the first datagram a WebTransport lane sends — binds the
 *  session to its seat by room code + seat token (the same token that already
 *  grants seat reclaim, M69 — one trust model, two transports). */
export const FRAME_WT_HELLO = 0x03;

/** M143: a WebTransport-lane ping — the client sends [0x04, f64 timestamp] and
 *  the server echoes the same bytes straight back, so the client can measure
 *  the datagram lane's OWN round trip (the WebSocket ping reads high whenever
 *  TCP is retransmitting — exactly when the lane matters most). */
export const FRAME_WT_PING = 0x04;

/** Send one batch every N sim ticks (120 Hz / 4 = 30 messages/s). */
export const INPUT_SEND_EVERY = 4;
/** Ticks of redundant input per batch (≥ INPUT_SEND_EVERY so nothing is lost).
 *  M69: 8 → 48 (~400 ms). 66 ms of redundancy died on any real phone-radio
 *  stall — the reverse press inside the gap was gone forever and the server
 *  extrapolated the stale forward input instead. 48 ticks is still only
 *  ~150 B per batch at 30 msg/s (~4.5 kB/s up). */
export const INPUT_BATCH_SIZE = 48;

const RACE_STATES: readonly RaceState[] = ['countdown', 'racing', 'finished'];
const TACKLE_PHASES = ['chase', 'slide'] as const;

const kindToByte = (k: PowerupKind | null): number => (k === null ? 0 : POWERUP_KINDS.indexOf(k) + 1);
const byteToKind = (b: number): PowerupKind | null => (b === 0 ? null : (POWERUP_KINDS[b - 1] ?? null));

const vehToByte = (v: string): number =>
  Math.max(0, (VEHICLE_ROSTER as readonly string[]).indexOf(v));
const byteToVeh = (b: number): string => VEHICLE_ROSTER[b] ?? VEHICLE_ROSTER[0];

/** Clamp+round a -1..1 axis to the i8 wire grid, back in float form. The client
 *  MUST predict with this value (see the determinism rule above). */
const qAxis = (v: number): number => Math.round(Math.max(-1, Math.min(1, v)) * 127) / 127;

export function quantizeInput(input: RacerInput): RacerInput {
  return { throttle: qAxis(input.throttle), steer: qAxis(input.steer), useItem: input.useItem };
}

/** True when a received ws frame is a binary snapshot (Game's lifecycle layer
 *  only needs the kind — the driver does the full decode). */
export function isSnapshotFrame(data: ArrayBuffer): boolean {
  return data.byteLength > 0 && new DataView(data).getUint8(0) === FRAME_SNAPSHOT;
}

// ---- tiny cursor helpers ----------------------------------------------------

class Writer {
  private readonly buf: ArrayBuffer;
  private view: DataView;
  private off = 0;
  constructor(capacity: number) {
    this.buf = new ArrayBuffer(capacity);
    this.view = new DataView(this.buf);
  }
  u8(v: number): void {
    this.view.setUint8(this.off, v);
    this.off += 1;
  }
  i8(v: number): void {
    this.view.setInt8(this.off, v);
    this.off += 1;
  }
  i16(v: number): void {
    this.view.setInt16(this.off, v, true);
    this.off += 2;
  }
  u16(v: number): void {
    this.view.setUint16(this.off, v, true);
    this.off += 2;
  }
  u32(v: number): void {
    this.view.setUint32(this.off, v >>> 0, true);
    this.off += 4;
  }
  f32(v: number): void {
    this.view.setFloat32(this.off, v, true);
    this.off += 4;
  }
  /** The written bytes, exact-size (a copy — the scratch stays reusable). */
  done(): ArrayBuffer {
    return this.buf.slice(0, this.off);
  }
}

class Reader {
  private view: DataView;
  private off = 0;
  constructor(buf: ArrayBuffer) {
    this.view = new DataView(buf);
  }
  u8(): number {
    return this.view.getUint8(this.off++);
  }
  i8(): number {
    return this.view.getInt8(this.off++);
  }
  i16(): number {
    const v = this.view.getInt16(this.off, true);
    this.off += 2;
    return v;
  }
  u16(): number {
    const v = this.view.getUint16(this.off, true);
    this.off += 2;
    return v;
  }
  u32(): number {
    const v = this.view.getUint32(this.off, true);
    this.off += 4;
    return v;
  }
  f32(): number {
    const v = this.view.getFloat32(this.off, true);
    this.off += 4;
    return v;
  }
}

// ---- snapshot ----------------------------------------------------------------

/** Generous upper bound: 8 racers, 8 walls, 80 pickups all fit with headroom. */
const SNAP_CAPACITY = 4096;

export function encodeSnapshot(snap: Snapshot): ArrayBuffer {
  const w = new Writer(SNAP_CAPACITY);
  w.u8(FRAME_SNAPSHOT);
  w.u32(snap.tick);
  w.u8(Math.max(0, RACE_STATES.indexOf(snap.state)));
  w.f32(snap.raceTime);
  w.f32(snap.countdown);
  w.f32(snap.goFlash);

  const acks = Object.entries(snap.ack);
  w.u8(acks.length);
  for (const [slot, tick] of acks) {
    w.u8(Number(slot));
    w.u32(tick);
  }

  // M69: per-human input-arrival margins (the clock-sync feedback signal).
  const margins = Object.entries(snap.margins);
  w.u8(margins.length);
  for (const [slot, m] of margins) {
    w.u8(Number(slot));
    // i16 since M143 (was i8, clamped at ±127): the client's clock controller
    // needs the EXACT newest tick the server has seen (snapshot tick + margin)
    // to tell feedback that already reflects a clock adjustment from feedback
    // that predates it. A clamped value made that test wrong precisely when
    // the clock was furthest off.
    w.i16(Math.max(-32767, Math.min(32767, Math.round(m))));
  }

  w.u8(snap.racers.length);
  for (const r of snap.racers) {
    w.u8(r.id);
    w.f32(r.car.x);
    w.f32(r.car.z);
    w.f32(r.car.heading);
    w.f32(r.car.vx);
    w.f32(r.car.vz);
    w.f32(r.car.steerAngle);
    w.f32(r.cy);
    w.f32(r.cvy);
    w.u8(r.cair ? 1 : 0);
    w.i16(r.chint);
    w.f32(r.ball.x);
    w.f32(r.ball.y);
    w.f32(r.ball.z);
    w.f32(r.ball.vx);
    w.f32(r.ball.vy);
    w.f32(r.ball.vz);
    w.f32(r.ball.radius);
    w.i16(r.bhint);
    w.u8(kindToByte(r.powerups.held));
    w.f32(r.powerups.roulette);
    w.u8(kindToByte(r.powerups.activeKind));
    w.f32(r.powerups.activeLeft);
    w.u8(r.finished ? 1 : 0);
    w.i8(r.finishOrder);
    w.f32(r.finishTime === null ? -1 : r.finishTime);
    w.f32(r.stunTimer);
    w.f32(r.stunSteer);
    w.f32(r.padBoost);
    w.f32(r.launchMagnet); // M117c: launch-ramp ball-magnet seconds left
    w.f32(r.scorpionTimer);
    w.f32(r.scorpionBallTimer);
    w.u8(vehToByte(r.vehicle));
    w.i8(r.carCh); // M117: secret-shortcut channel indices (−1 = the road)
    w.i8(r.ballCh);
  }

  if (snap.tackle) {
    w.u8(1);
    w.u8(snap.tackle.firerId);
    w.u8(snap.tackle.targetId);
    w.f32(snap.tackle.dist);
    w.f32(snap.tackle.lat);
    w.u8(snap.tackle.phase === 'slide' ? 1 : 0);
    w.f32(snap.tackle.timer);
    w.f32(snap.tackle.heading);
  } else {
    w.u8(0);
  }

  w.u8(snap.walls.length);
  for (const wall of snap.walls) {
    w.f32(wall.x);
    w.f32(wall.z);
    w.f32(wall.dirX);
    w.f32(wall.dirZ);
    w.f32(wall.rightX);
    w.f32(wall.rightZ);
    w.f32(wall.halfSpan);
    w.u8(wall.ownerId);
    w.u32(wall.color);
    w.f32(wall.y);
    w.f32(wall.timer);
  }

  w.u8(snap.mud.length);
  for (const m of snap.mud) {
    w.u8(m.ownerId);
    w.f32(m.x);
    w.f32(m.y);
    w.f32(m.z);
    w.f32(m.radius);
    w.u32(m.color);
    w.f32(m.timer);
  }

  w.u16(snap.pickups.length);
  for (const respawn of snap.pickups) w.f32(respawn);

  const inputs = Object.entries(snap.inputs);
  w.u8(inputs.length);
  for (const [slot, input] of inputs) {
    w.u8(Number(slot));
    w.i8(Math.round(Math.max(-1, Math.min(1, input.throttle)) * 127));
    w.i8(Math.round(Math.max(-1, Math.min(1, input.steer)) * 127));
    w.u8(input.useItem ? 1 : 0);
  }

  return w.done();
}

export function decodeSnapshot(buf: ArrayBuffer): Snapshot {
  const r = new Reader(buf);
  const kind = r.u8();
  if (kind !== FRAME_SNAPSHOT) throw new Error(`not a snapshot frame (kind ${kind})`);
  const tick = r.u32();
  const state = RACE_STATES[r.u8()] ?? 'racing';
  const raceTime = r.f32();
  const countdown = r.f32();
  const goFlash = r.f32();

  const ack: Record<number, number> = {};
  const ackCount = r.u8();
  for (let i = 0; i < ackCount; i++) {
    const slot = r.u8();
    ack[slot] = r.u32();
  }

  const margins: Record<number, number> = {};
  const marginCount = r.u8();
  for (let i = 0; i < marginCount; i++) {
    const slot = r.u8();
    margins[slot] = r.i16();
  }

  const racers: RacerSnapshot[] = [];
  const racerCount = r.u8();
  for (let i = 0; i < racerCount; i++) {
    const id = r.u8();
    const car = {
      x: r.f32(),
      z: r.f32(),
      heading: r.f32(),
      vx: r.f32(),
      vz: r.f32(),
      steerAngle: r.f32(),
    };
    const cy = r.f32();
    const cvy = r.f32();
    const cair = r.u8() === 1;
    const chint = r.i16();
    const ball = {
      x: r.f32(),
      y: r.f32(),
      z: r.f32(),
      vx: r.f32(),
      vy: r.f32(),
      vz: r.f32(),
      radius: r.f32(),
    };
    const bhint = r.i16();
    const powerups = {
      held: byteToKind(r.u8()),
      roulette: r.f32(),
      activeKind: byteToKind(r.u8()),
      activeLeft: r.f32(),
    };
    const finished = r.u8() === 1;
    const finishOrder = r.i8();
    const ft = r.f32();
    const finishTime = ft < 0 ? null : ft;
    const stunTimer = r.f32();
    const stunSteer = r.f32();
    const padBoost = r.f32();
    const launchMagnet = r.f32(); // M117c
    const scorpionTimer = r.f32();
    const scorpionBallTimer = r.f32();
    const vehicle = byteToVeh(r.u8());
    const carCh = r.i8(); // M117: secret-shortcut channel indices (−1 = the road)
    const ballCh = r.i8();
    racers.push({
      id,
      car,
      cy,
      cvy,
      cair,
      chint,
      ball,
      bhint,
      powerups,
      finished,
      finishOrder,
      finishTime,
      stunTimer,
      stunSteer,
      padBoost,
      launchMagnet,
      scorpionTimer,
      scorpionBallTimer,
      vehicle,
      carCh,
      ballCh,
    });
  }

  let tackle: TackleState | null = null;
  if (r.u8() === 1) {
    tackle = {
      firerId: r.u8(),
      targetId: r.u8(),
      dist: r.f32(),
      lat: r.f32(),
      phase: TACKLE_PHASES[0],
      timer: 0,
      heading: 0,
    };
    tackle.phase = r.u8() === 1 ? 'slide' : 'chase';
    tackle.timer = r.f32();
    tackle.heading = r.f32();
  }

  const walls: WallDrop[] = [];
  const wallCount = r.u8();
  for (let i = 0; i < wallCount; i++) {
    walls.push({
      x: r.f32(),
      z: r.f32(),
      dirX: r.f32(),
      dirZ: r.f32(),
      rightX: r.f32(),
      rightZ: r.f32(),
      halfSpan: r.f32(),
      ownerId: r.u8(),
      color: r.u32(),
      y: r.f32(),
      timer: r.f32(),
    });
  }

  const mud: MudDrop[] = [];
  const mudCount = r.u8();
  for (let i = 0; i < mudCount; i++) {
    mud.push({
      ownerId: r.u8(),
      x: r.f32(),
      y: r.f32(),
      z: r.f32(),
      radius: r.f32(),
      color: r.u32(),
      timer: r.f32(),
    });
  }

  const pickups: number[] = [];
  const pickupCount = r.u16();
  for (let i = 0; i < pickupCount; i++) pickups.push(r.f32());

  const inputs: Record<number, RacerInput> = {};
  const inputCount = r.u8();
  for (let i = 0; i < inputCount; i++) {
    const slot = r.u8();
    inputs[slot] = { throttle: r.i8() / 127, steer: r.i8() / 127, useItem: r.u8() === 1 };
  }

  return { tick, state, raceTime, countdown, goFlash, ack, racers, tackle, walls, mud, pickups, inputs, margins };
}

// ---- input batch ---------------------------------------------------------------

export interface InputBatchEntry {
  tick: number;
  input: RacerInput;
}

/** Entries must be consecutive ticks, oldest first (the sender's ring buffer). */
export function encodeInputBatch(entries: InputBatchEntry[]): ArrayBuffer {
  const w = new Writer(8 + entries.length * 3);
  w.u8(FRAME_INPUT_BATCH);
  w.u32(entries.length ? entries[0].tick : 0);
  w.u8(entries.length);
  for (const e of entries) {
    w.i8(Math.round(Math.max(-1, Math.min(1, e.input.throttle)) * 127));
    w.i8(Math.round(Math.max(-1, Math.min(1, e.input.steer)) * 127));
    w.u8(e.input.useItem ? 1 : 0);
  }
  return w.done();
}

export function decodeInputBatch(buf: ArrayBuffer): InputBatchEntry[] {
  const r = new Reader(buf);
  const kind = r.u8();
  if (kind !== FRAME_INPUT_BATCH) throw new Error(`not an input batch (kind ${kind})`);
  const firstTick = r.u32();
  const count = r.u8();
  const out: InputBatchEntry[] = [];
  for (let i = 0; i < count; i++) {
    out.push({
      tick: firstTick + i,
      input: { throttle: r.i8() / 127, steer: r.i8() / 127, useItem: r.u8() === 1 },
    });
  }
  return out;
}

/** V2 Phase C (M108): the WebTransport lane's hello — [0x03, roomLen, room…,
 *  tokLen, tok…] (utf-8, each capped 32 bytes). The lane is DATA-only after
 *  binding: snapshots down, input batches up — all control stays on the WS. */
export function encodeWtHello(room: string, tok: string): ArrayBuffer {
  const enc = new TextEncoder();
  const r = enc.encode(room).slice(0, 32);
  const t = enc.encode(tok).slice(0, 32);
  const out = new Uint8Array(3 + r.length + t.length);
  out[0] = FRAME_WT_HELLO;
  out[1] = r.length;
  out.set(r, 2);
  out[2 + r.length] = t.length;
  out.set(t, 3 + r.length);
  return out.buffer;
}

export function decodeWtHello(buf: ArrayBuffer): { room: string; tok: string } | null {
  const b = new Uint8Array(buf);
  if (b.length < 3 || b[0] !== FRAME_WT_HELLO) return null;
  const rLen = b[1];
  if (b.length < 3 + rLen) return null;
  const tLen = b[2 + rLen];
  if (b.length < 3 + rLen + tLen) return null;
  const dec = new TextDecoder();
  return {
    room: dec.decode(b.subarray(2, 2 + rLen)),
    tok: dec.decode(b.subarray(3 + rLen, 3 + rLen + tLen)),
  };
}

/** M143: the lane ping (see FRAME_WT_PING). `ts` is the sender's clock in ms. */
export function encodeWtPing(ts: number): ArrayBuffer {
  const out = new ArrayBuffer(9);
  const v = new DataView(out);
  v.setUint8(0, FRAME_WT_PING);
  v.setFloat64(1, ts, true);
  return out;
}

/** The timestamp an echoed lane ping carries, or null if this is not one. */
export function decodeWtPing(buf: ArrayBuffer): number | null {
  if (buf.byteLength !== 9) return null;
  const v = new DataView(buf);
  return v.getUint8(0) === FRAME_WT_PING ? v.getFloat64(1, true) : null;
}
